import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { Specialty } from '../models/specialty.model';
import { CountryISO } from 'ngx-material-intl-tel-input';
import esArTranslations from '../../assets/i18n/es-ar.json';
import esTranslations from '../../assets/i18n/es.json';
import ptTranslations from '../../assets/i18n/pt.json';
import enTranslations from '../../assets/i18n/en.json';
import { Provider } from '../models/provider.model';
import { Language } from '../models/language.model';

interface Translation {
  [key: string]: string;
}

const TRANSLATIONS_MAP: Record<string, Translation> = {
  'es-ar': esArTranslations,
  es: esTranslations,
  pt: ptTranslations,
  en: enTranslations
};

@Injectable({
  providedIn: 'root'
})
export class LocaleService {
  translations!: Translation;
  locale!: string;
  defaultLocale = 'en';
  supportedLocales = ['es-ar', 'es', 'pt', 'en'];

  use(locale: string): Promise<Translation> {
    const translation = TRANSLATIONS_MAP[locale];
    if (translation) {
      this.translations = translation;
    } else {
      console.warn(`Locale ${locale} not found, using fallback.`);
      this.translations = esArTranslations;
    }
    return Promise.resolve(this.translations);
  }

  getString(key: string): string {
    return this.translations[key] || key;
  }

  getLocalizedText(texts?: Record<string, string>): string {
    if (!texts) return '';

    const locale = this.getLocale().toLowerCase();
    const preferredLanguages = [locale, locale.split('-')[0], 'es-ar', 'es', 'en', 'pt'];
    for (const language of preferredLanguages) {
      const text = texts[language]?.trim();
      if (text) return text;
    }

    return Object.values(texts).find((text) => typeof text === 'string' && text.trim())?.trim() || '';
  }

  getLocale() {
    return this.locale || this.defaultLocale;
  }

  setLocale(locale: string) {
    this.locale = locale;
  }

  setupTranslate(): Promise<Translation> {
    let locale: string | string[] | null = this.getLocaleFromLocalStorage();

    const providerStorage = localStorage.getItem('provider');
    const provider = providerStorage ? (JSON.parse(providerStorage) as Provider) : null;
    if (_.isEmpty(locale)) return this.setLocaleInStorage([...window.navigator.languages], 'session', provider);

    console.log('Using saved locale: ', locale);
    this.setLocale(locale!);
    return this.use(locale!);
  }

  setLocaleInStorage(locale: string | string[], storage: string, provider: Provider | null) {
    this.locale = this.resolveLocale(locale, provider);

    switch (storage) {
      case 'session':
        sessionStorage.setItem('locale', this.locale);
        break;
      case 'local':
        localStorage.setItem('locale', this.locale);
        break;
    }

    return this.use(this.locale);
  }

  private resolveLocale(locale: string | string[], provider: Provider | null): string {
    const locales = Array.isArray(locale) ? locale : [locale];

    for (const loc of locales) {
      const candidateLocale = loc.toLowerCase();
      const localeShort = candidateLocale.split('-')[0];
      if (this.isLocaleSupported(candidateLocale, provider)) return candidateLocale;
      if (this.isLocaleSupported(localeShort, provider)) return localeShort;
    }

    const providerLocale = this.getMainProviderLocale(provider);
    if (providerLocale) {
      console.log('Using main provider locale: ', providerLocale);
      return providerLocale;
    }

    console.log(locale + ' not supported, using default: ', this.defaultLocale);
    return this.defaultLocale;
  }

  private isLocaleSupported(locale: string, provider: Provider | null): boolean {
    const supported = this.supportedLocales.includes(locale) && this.providerSupportsLocale(locale, provider);
    if (supported) console.log('Using navigator locale: ', locale);
    return supported;
  }

  private providerSupportsLocale(locale: string, provider: Provider | null): boolean {
    // Fallback to PWA supported locales if provider languages are not defined
    if (_.isEmpty(provider?.supportedLanguages)) return true;
    return !!provider?.supportedLanguages?.some((language: Language) => language.code === locale);
  }

  private getMainProviderLocale(provider: Provider | null): string | null {
    if (!provider?.language) return null;

    if (this.supportedLocales.includes(provider.language)) return provider.language;
    const providerLocaleShort = provider.language.split('-')[0];
    if (this.supportedLocales.includes(providerLocaleShort)) return providerLocaleShort;
    return null;
  }

  setLocaleIdInStorage(id: string) {
    return localStorage.setItem('localeId', id);
  }

  getLocaleIdInStorage() {
    return localStorage.getItem('localeId');
  }

  getLocaleFromLocalStorage() {
    return localStorage.getItem('locale');
  }

  getLocaleFromSessionStorage() {
    return sessionStorage.getItem('locale');
  }

  public getHeaderLocale(): string {
    //We send only language headers thar are accepted by the API
    //That is because we use sails i18n to translate messages, to see documentation, visit https://0.12.sailsjs.com/documentation/concepts/internationalization
    switch (this.locale) {
      case 'pt':
        return 'pt-BR';
      case 'es':
        return 'es-CO';
      case 'en':
        return 'en-US';
      default:
        return 'es-AR';
    }
  }

  public getLanguageISO(): string {
    const locale = this.getLocaleFromLocalStorage() ?? this.getLocaleFromSessionStorage() ?? this.defaultLocale;
    return locale.split('-')[0];
  }

  getCountryISO(language: string = this.getLocale()): CountryISO {
    switch (language) {
      case 'en':
        return CountryISO.UnitedStates;
      case 'pt':
        return CountryISO.Brazil;
      default:
        return CountryISO.Argentina;
    }
  }

  getSpecialtyName(value: Specialty): string | undefined {
    if (typeof value === 'string' || !value) return undefined;

    const locale = this.getLocale();
    let localizedName = value.localizedName[locale];
    if (!localizedName) {
      for (const [key] of Object.entries(value.localizedName)) {
        if (key.includes(locale)) {
          localizedName = value.localizedName[key];
        }
      }
    }
    // Adding spaces to break the line with CSS
    return !localizedName ? '' : localizedName.replace('/', ' / ');
  }
}
